import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';
import Button from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { ui_issueTableGroupByDropdown_GroupByDropdown$key } from '@atlassian/jira-relay/src/__generated__/ui_issueTableGroupByDropdown_GroupByDropdown.graphql';
import { TEAM_NAME, PACKAGE_NAME } from '../common/constants.tsx';
import { LoadingContent } from '../common/ui/loading-content/index.tsx';
import { groupByDropdownEntryPoint } from './dropdown-content/entrypoint.tsx';
import messages from './messages.tsx';

export type GroupByDropdownProps = {
	spreadsheetView: ui_issueTableGroupByDropdown_GroupByDropdown$key;
};

export const GroupByDropdown = ({ spreadsheetView }: GroupByDropdownProps) => {
	const { formatMessage } = useIntl();

	const data = useFragment<ui_issueTableGroupByDropdown_GroupByDropdown$key>(
		graphql`
			fragment ui_issueTableGroupByDropdown_GroupByDropdown on JiraSpreadsheetView {
				id
				viewSettings @required(action: THROW) {
					groupByConfig @required(action: THROW) {
						groupByField {
							name
						}
					}
				}
			}
		`,
		spreadsheetView,
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		groupByDropdownEntryPoint,
		{ nodeId: data?.id },
	);
	const buttonTrigger = useEntryPointButtonTrigger(entryPointActions);
	const { groupByField } = data?.viewSettings?.groupByConfig || {};

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...triggerProps }) => (
				<Button {...triggerProps} ref={mergeRefs([triggerRef, buttonTrigger])}>
					{groupByField?.name
						? formatMessage(messages.groupByLabelWithSelection, {
								groupByField: groupByField.name,
							})
						: formatMessage(messages.groupByLabel)}
				</Button>
			)}
			placement="bottom-end"
			shouldRenderToParent
			spacing="compact"
		>
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="async-jira-spreadsheet-group-by-dropdown"
				packageName={PACKAGE_NAME}
				errorFallback="flag"
				teamName={TEAM_NAME}
				runtimeProps={{ nodeId: data.id }}
				fallback={<LoadingContent />}
			/>
		</DropdownMenu>
	);
};
